// @ts-nocheck
import {useState, useEffect, useRef} from 'react'
import {toCdnUrl} from '../../utils/cdnUrl'
import {PageTitle} from '../../../_metronic/layout/core'
import {th3postLogin, th3postPreview, th3postImport, th3postImportStatus, th3postLogout, th3postGetSavedCredentials} from './core/_requests'
import {Th3PostCampaignPreview, Th3PostPreviewResponse, Th3PostImportStatus} from './core/_models'

function Th3PostSyncWrapper() {
  const [step, setStep] = useState<'login' | 'preview' | 'importing' | 'complete'>('login')

  // Login state
  const [cookie, setCookie] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const [loginError, setLoginError] = useState('')

  // Preview state
  const [previewData, setPreviewData] = useState<Th3PostPreviewResponse | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [showImagesId, setShowImagesId] = useState<string | null>(null)

  // Import state
  const [importStatus, setImportStatus] = useState<Th3PostImportStatus | null>(null)
  const [importError, setImportError] = useState('')
  const pollRef = useRef<NodeJS.Timeout | null>(null)

  const [credentialsLoading, setCredentialsLoading] = useState(true)

  // Load saved credentials on mount
  useEffect(() => {
    th3postGetSavedCredentials().then(data => {
      if (data.hasSaved) {
        setCookie(data.cookie)
      }
    }).catch(() => {}).finally(() => setCredentialsLoading(false))
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoginError('')
    setLoginLoading(true)
    try {
      await th3postLogin(cookie)
      setStep('preview')
      loadPreview()
    } catch (err: any) {
      setLoginError(err.response?.data?.error || 'שגיאה בהתחברות')
    } finally {
      setLoginLoading(false)
    }
  }

  const loadPreview = async () => {
    setPreviewLoading(true)
    setPreviewError('')
    try {
      const data = await th3postPreview()
      setPreviewData(data)
      const ids = new Set<string>()
      data.campaigns.forEach(c => {
        if (!c.isArchived) ids.add(c.id)
      })
      setSelectedIds(ids)
    } catch (err: any) {
      if (err.response?.data?.error === 'SESSION_EXPIRED') {
        setStep('login')
        setLoginError('החיבור ל-Th3Post פג תוקף, נא להתחבר מחדש')
      } else {
        setPreviewError(err.response?.data?.error || 'שגיאה בטעינת נתונים')
      }
    } finally {
      setPreviewLoading(false)
    }
  }

  const toggleCampaign = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (!previewData) return
    if (selectedIds.size === previewData.campaigns.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(previewData.campaigns.map(c => c.id)))
    }
  }

  const handleImport = async () => {
    setImportError('')
    setStep('importing')
    try {
      const ids = Array.from(selectedIds)
      const { jobId } = await th3postImport(ids)

      pollRef.current = setInterval(async () => {
        try {
          const status = await th3postImportStatus(jobId)
          setImportStatus(status)
          if (status.status === 'completed' || status.status === 'error') {
            if (pollRef.current) clearInterval(pollRef.current)
            if (status.status === 'completed') {
              setStep('complete')
            } else {
              setImportError(status.errors.join(', ') || 'שגיאה בייבוא')
            }
          }
        } catch (pollErr) {
          console.error('Poll error:', pollErr)
        }
      }, 2000)
    } catch (err: any) {
      setImportError(err.response?.data?.error || 'שגיאה בהתחלת הייבוא')
      setStep('preview')
    }
  }

  const handleLogout = async () => {
    try { await th3postLogout() } catch (e) {}
    setStep('login')
    setPreviewData(null)
    setImportStatus(null)
    setCookie('')
  }

  return (
    <>
      <PageTitle>ייבוא מ-Th3Post</PageTitle>
      <div className='container-xxl' dir='rtl'>

        {/* Step 1: Login */}
        {step === 'login' && (
          <div className='row justify-content-center'>
            <div className='col-md-6 col-lg-5'>
              <div className='card shadow-sm'>
                <div className='card-header border-0 pt-8 pb-4'>
                  <div className='text-center w-100'>
                    <h2 className='fw-bold mb-2'>התחברות ל-Th3Post</h2>
                    <p className='text-muted fs-6'>הדבק את ה-Cookie מהדפדפן כדי לייבא קמפיינים מ-Th3Post</p>
                  </div>
                </div>
                <div className='card-body pt-0'>
                  {credentialsLoading ? (
                    <div className='text-center py-10'>
                      <div className='spinner-border spinner-border-sm text-muted'></div>
                    </div>
                  ) : <form onSubmit={handleLogin} autoComplete='off'>
                    {loginError && (
                      <div className='alert alert-danger d-flex align-items-center py-3 mb-5'>
                        <i className='bi bi-exclamation-triangle-fill me-3 fs-4'></i>
                        <span>{loginError}</span>
                      </div>
                    )}
                    <div className='mb-5'>
                      <label className='form-label fw-semibold'>Cookie</label>
                      <textarea
                        className='form-control form-control-lg'
                        placeholder='_fbp=...; auth_session=...; ...'
                        value={cookie}
                        onChange={e => setCookie(e.target.value)}
                        required
                        dir='ltr'
                        autoComplete='off'
                        name='th3post-cookie-field'
                        rows={4}
                        style={{fontSize: '0.85rem'}}
                      />
                      <div className='form-text text-muted mt-2'>
                        העתק את ה-Cookie מהדפדפן (F12 → Application → Cookies) וודא שהוא מכיל auth_session
                      </div>
                    </div>
                    <button
                      type='submit'
                      className='btn btn-primary w-100 py-3'
                      disabled={loginLoading}
                    >
                      {loginLoading ? (
                        <span className='spinner-border spinner-border-sm me-2'></span>
                      ) : (
                        <i className='bi bi-box-arrow-in-left me-2'></i>
                      )}
                      התחבר
                    </button>
                  </form>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 'preview' && (
          <div className='row'>
            <div className='col-12'>
              {previewLoading ? (
                <div className='card shadow-sm'>
                  <div className='card-body text-center py-20'>
                    <div className='spinner-border text-primary mb-5' style={{width: '3rem', height: '3rem'}}></div>
                    <p className='fs-5 text-muted'>טוען נתונים מ-Th3Post...</p>
                    <p className='text-muted fs-7'>זה עשוי לקחת דקה או שתיים</p>
                  </div>
                </div>
              ) : previewError ? (
                <div className='card shadow-sm'>
                  <div className='card-body text-center py-15'>
                    <div className='alert alert-danger'>{previewError}</div>
                    <button className='btn btn-light-primary' onClick={loadPreview}>נסה שוב</button>
                  </div>
                </div>
              ) : previewData && (
                <>
                  {/* Summary cards */}
                  <div className='row g-4 mb-6'>
                    <div className='col-6 col-md-3'>
                      <div className='card shadow-sm h-100'>
                        <div className='card-body text-center py-6'>
                          <div className='fs-1 fw-bolder text-primary'>{previewData.summary.totalCampaigns}</div>
                          <div className='text-muted fw-semibold fs-7'>קמפיינים</div>
                        </div>
                      </div>
                    </div>
                    <div className='col-6 col-md-3'>
                      <div className='card shadow-sm h-100'>
                        <div className='card-body text-center py-6'>
                          <div className='fs-1 fw-bolder text-info'>{previewData.summary.totalImages}</div>
                          <div className='text-muted fw-semibold fs-7'>תמונות</div>
                        </div>
                      </div>
                    </div>
                    <div className='col-6 col-md-3'>
                      <div className='card shadow-sm h-100'>
                        <div className='card-body text-center py-6'>
                          <div className='fs-1 fw-bolder text-success'>{previewData.summary.matchedGroups}</div>
                          <div className='text-muted fw-semibold fs-7'>קבוצות מותאמות</div>
                        </div>
                      </div>
                    </div>
                    <div className='col-6 col-md-3'>
                      <div className='card shadow-sm h-100'>
                        <div className='card-body text-center py-6'>
                          <div className='fs-1 fw-bolder text-warning'>{previewData.summary.newGroups}</div>
                          <div className='text-muted fw-semibold fs-7'>קבוצות חדשות</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Campaign list */}
                  <div className='card shadow-sm'>
                    <div className='card-header border-0 pt-6'>
                      <div className='d-flex justify-content-between align-items-center w-100'>
                        <h3 className='fw-bold m-0'>
                          קמפיינים ({selectedIds.size} נבחרו מתוך {previewData.campaigns.length})
                        </h3>
                        <div className='d-flex gap-3'>
                          <button className='btn btn-sm btn-light-secondary' onClick={handleLogout}>
                            <i className='bi bi-box-arrow-right me-1'></i>
                            התנתק
                          </button>
                          <button
                            className='btn btn-sm btn-primary'
                            onClick={handleImport}
                            disabled={selectedIds.size === 0}
                          >
                            <i className='bi bi-download me-1'></i>
                            ייבא {selectedIds.size} קמפיינים
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className='card-body pt-0'>
                      <div className='table-responsive'>
                        <table className='table table-row-bordered table-row-gray-200 align-middle gy-3'>
                          <thead>
                            <tr className='fw-semibold text-muted'>
                              <th style={{width: 40}}>
                                <div className='form-check form-check-sm'>
                                  <input
                                    className='form-check-input'
                                    type='checkbox'
                                    checked={selectedIds.size === previewData.campaigns.length}
                                    onChange={toggleAll}
                                  />
                                </div>
                              </th>
                              <th>שם</th>
                              <th>תוכן</th>
                              <th style={{width: 100}}>תמונות</th>
                              <th>ספריות קבוצות</th>
                              <th style={{width: 80}}>סטטוס</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.campaigns.map(c => (
                              <>
                              <tr key={c.id} className={c.isArchived ? 'opacity-50' : ''}>
                                <td>
                                  <div className='form-check form-check-sm'>
                                    <input
                                      className='form-check-input'
                                      type='checkbox'
                                      checked={selectedIds.has(c.id)}
                                      onChange={() => toggleCampaign(c.id)}
                                    />
                                  </div>
                                </td>
                                <td>
                                  <span className='fw-semibold text-gray-800'>{c.name}</span>
                                  {c.extraContents > 0 && (
                                    <span className='badge badge-light-info ms-2'>+{c.extraContents} גרסאות</span>
                                  )}
                                </td>
                                <td>
                                  <div
                                    className='text-muted fs-7 cursor-pointer'
                                    style={expandedId === c.id ? {
                                      maxWidth: 400,
                                      whiteSpace: 'pre-wrap',
                                    } : {
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      maxWidth: 400,
                                    }}
                                    onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                                    title='לחץ להרחבה'
                                  >
                                    {c.fullContent || '(ללא תוכן)'}
                                  </div>
                                </td>
                                <td>
                                  {c.imageCount > 0 ? (
                                    <button
                                      className='btn btn-sm btn-light-primary py-1 px-3'
                                      onClick={() => setShowImagesId(showImagesId === c.id ? null : c.id)}
                                    >
                                      <i className='bi bi-image me-1'></i>
                                      {c.imageCount}
                                    </button>
                                  ) : (
                                    <span className='text-muted'>-</span>
                                  )}
                                </td>
                                <td>
                                  {c.templateInfo && c.templateInfo.length > 0 ? (
                                    <div className='d-flex flex-wrap gap-1'>
                                      {c.templateInfo.map((t, i) => (
                                        <span key={i} className='badge badge-light-success'>
                                          {t.name} ({t.groupCount} קבוצות)
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className='text-muted'>-</span>
                                  )}
                                </td>
                                <td>
                                  {c.isArchived ? (
                                    <span className='badge badge-light-warning'>ארכיון</span>
                                  ) : (
                                    <span className='badge badge-light-success'>פעיל</span>
                                  )}
                                </td>
                              </tr>
                              {/* Image preview row */}
                              {showImagesId === c.id && c.imageUrls && c.imageUrls.length > 0 && (
                                <tr key={c.id + '-images'}>
                                  <td colSpan={6}>
                                    <div className='d-flex flex-wrap gap-3 py-2'>
                                      {c.imageUrls.map((url, i) => (
                                        <a key={i} href={url} target='_blank' rel='noopener noreferrer'>
                                          <img
                                            src={toCdnUrl(url)}
                                            alt={`תמונה ${i + 1}`}
                                            style={{
                                              width: 120,
                                              height: 120,
                                              objectFit: 'cover',
                                              borderRadius: 8,
                                              border: '1px solid #eee',
                                            }}
                                          />
                                        </a>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              )}
                              </>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Import Progress */}
        {step === 'importing' && (
          <div className='row justify-content-center'>
            <div className='col-md-8 col-lg-6'>
              <div className='card shadow-sm'>
                <div className='card-body text-center py-15'>
                  <div className='spinner-border text-primary mb-5' style={{width: '3rem', height: '3rem'}}></div>
                  <h3 className='fw-bold mb-5'>{importStatus?.currentStep || 'מתחיל ייבוא...'}</h3>

                  {importStatus && (
                    <>
                      {/* Campaign progress */}
                      <div className='mb-5'>
                        <div className='d-flex justify-content-between mb-2'>
                          <span className='text-muted fs-7'>קמפיינים</span>
                          <span className='fw-semibold'>{importStatus.importedCampaigns}/{importStatus.totalCampaigns}</span>
                        </div>
                        <div className='progress' style={{height: 8}}>
                          <div
                            className='progress-bar bg-primary'
                            style={{
                              width: importStatus.totalCampaigns > 0
                                ? `${(importStatus.importedCampaigns / importStatus.totalCampaigns) * 100}%`
                                : '0%'
                            }}
                          ></div>
                        </div>
                      </div>

                      {/* Image progress */}
                      <div className='mb-5'>
                        <div className='d-flex justify-content-between mb-2'>
                          <span className='text-muted fs-7'>תמונות</span>
                          <span className='fw-semibold'>{importStatus.uploadedImages}/{importStatus.totalImages}</span>
                        </div>
                        <div className='progress' style={{height: 8}}>
                          <div
                            className='progress-bar bg-info'
                            style={{
                              width: importStatus.totalImages > 0
                                ? `${(importStatus.uploadedImages / importStatus.totalImages) * 100}%`
                                : '0%'
                            }}
                          ></div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className='d-flex justify-content-center gap-5 mt-5'>
                        <div>
                          <div className='fs-4 fw-bolder text-success'>{importStatus.createdGroups}</div>
                          <div className='text-muted fs-8'>קבוצות חדשות</div>
                        </div>
                        <div>
                          <div className='fs-4 fw-bolder text-primary'>{importStatus.createdLibraries}</div>
                          <div className='text-muted fs-8'>ספריות קבוצות</div>
                        </div>
                      </div>
                    </>
                  )}

                  {importError && (
                    <div className='alert alert-danger mt-5'>{importError}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 'complete' && importStatus && (
          <div className='row justify-content-center'>
            <div className='col-md-8 col-lg-6'>
              <div className='card shadow-sm'>
                <div className='card-body text-center py-15'>
                  <div className='mb-5'>
                    <i className='bi bi-check-circle-fill text-success' style={{fontSize: '4rem'}}></i>
                  </div>
                  <h2 className='fw-bold mb-3'>הייבוא הושלם בהצלחה!</h2>
                  <p className='text-muted fs-6 mb-8'>כל הקמפיינים יובאו לקמפיין "posts from th3post"</p>

                  <div className='row g-4 mb-8'>
                    <div className='col-4'>
                      <div className='fs-2 fw-bolder text-primary'>{importStatus.importedCampaigns}</div>
                      <div className='text-muted fs-7'>קמפיינים</div>
                    </div>
                    <div className='col-4'>
                      <div className='fs-2 fw-bolder text-info'>{importStatus.uploadedImages}</div>
                      <div className='text-muted fs-7'>תמונות</div>
                    </div>
                    <div className='col-4'>
                      <div className='fs-2 fw-bolder text-success'>{importStatus.createdLibraries}</div>
                      <div className='text-muted fs-7'>ספריות קבוצות</div>
                    </div>
                  </div>

                  {importStatus.errors.length > 0 && (
                    <div className='alert alert-warning text-start mb-5'>
                      <h6 className='fw-bold mb-2'>
                        <i className='bi bi-exclamation-triangle me-2'></i>
                        שגיאות ({importStatus.errors.length})
                      </h6>
                      <ul className='mb-0 pe-3'>
                        {importStatus.errors.slice(0, 5).map((err, i) => (
                          <li key={i} className='fs-7'>{err}</li>
                        ))}
                        {importStatus.errors.length > 5 && (
                          <li className='fs-7 text-muted'>...ועוד {importStatus.errors.length - 5} שגיאות</li>
                        )}
                      </ul>
                    </div>
                  )}

                  <div className='d-flex justify-content-center gap-3'>
                    <a href='/campaigns' className='btn btn-primary'>
                      <i className='bi bi-folder me-2'></i>
                      עבור לקמפיינים
                    </a>
                    <button className='btn btn-light-secondary' onClick={handleLogout}>
                      סיום
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export {Th3PostSyncWrapper}
